import React, { useState, useEffect } from 'react';
import { Form, Input, Button, notification, Typography, Table, Row, Col, Popconfirm, Modal, Card, Space, Statistic } from 'antd';
import { UserAddOutlined, EditOutlined, DeleteOutlined, UserOutlined, PhoneOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_ENDPOINTS } from '../../../utils/constants';

const AddSupplier = () => {
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm(); // Form for editing supplier
    const [suppliers, setSuppliers] = useState([]);
    const [editingSupplier, setEditingSupplier] = useState(null); // Store the supplier being edited
    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        fetchSuppliers();
    }, []);

    const fetchSuppliers = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.GET_SUPPLIER);
            if (response.data.success) {
                setSuppliers(response.data.data);
            } else {
                notification.error({
                    message: 'Error',
                    description: response.data.message || 'An error occurred',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to fetch suppliers',
            });
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const response = await axios.post(API_ENDPOINTS.ADD_SUPPLIER, values);
            if (response.data.success) {
                notification.success({
                    message: 'Success',
                    description: 'Supplier added successfully',
                });
                form.resetFields();
                fetchSuppliers(); // Refresh the supplier list
            } else {
                notification.error({
                    message: 'Error',
                    description: response.data.message || 'An error occurred',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to add supplier',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (supplier) => {
        setEditingSupplier(supplier);
        editForm.setFieldsValue(supplier);
        setModalVisible(true);
    };

    const handleEditSubmit = async (values) => {
        setLoading(true);
        try {
            const response = await axios.post(API_ENDPOINTS.UPDATE_SUPPLIER, {
                ...values,
                id: editingSupplier.id
            });
            if (response.data.success) {
                notification.success({
                    message: 'Success',
                    description: 'Supplier updated successfully',
                });
                setModalVisible(false);
                fetchSuppliers(); // Refresh the supplier list
            } else {
                notification.error({
                    message: 'Error',
                    description: response.data.message || 'An error occurred',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to update supplier',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await axios.post(API_ENDPOINTS.DELETE_SUPPLIER, { id });
            if (response.data.success) {
                notification.success({
                    message: 'Success',
                    description: 'Supplier deleted successfully',
                });
                fetchSuppliers(); // Refresh the supplier list
            } else {
                notification.error({
                    message: 'Error',
                    description: response.data.message || 'An error occurred',
                });
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: 'Failed to delete supplier',
            });
        }
    };

    const columns = [
        {
            title: 'Supplier Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Contact Phone',
            dataIndex: 'number',
            key: 'number',
        },
        {
            title: 'Address',
            dataIndex: 'address',
            key: 'address',
        },
        {
            title: '#',
            width: 50,
            render: (_, __, index) => (
                <span style={{ fontWeight: 500, color: '#666' }}>{index + 1}</span>
            ),
        },
        {
            title: 'Actions',
            key: 'action',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete Supplier"
                        description="Are you sure you want to delete this supplier?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                    >
                        <Button
                            type="primary"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="fade-in">
            <Row gutter={[20, 20]} style={{ marginBottom: 28 }}>
                <Col xs={24} sm={12} lg={8}>
                    <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                        <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
                            <div>
                                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    TOTAL REGISTERED SUPPLIERS
                                </Typography.Text>
                                <Statistic
                                    value={suppliers.length}
                                    valueStyle={{ color: '#0F172A', fontWeight: 800, fontSize: '24px' }}
                                />
                            </div>
                            <div className="stat-icon-wrapper" style={{ background: '#EEF2FF', color: '#4F46E5' }}>
                                <UserOutlined />
                            </div>
                        </Space>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                        <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
                            <div>
                                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    ACTIVE VENDORS
                                </Typography.Text>
                                <Statistic
                                    value={suppliers.length}
                                    valueStyle={{ color: '#10B981', fontWeight: 800, fontSize: '24px' }}
                                />
                            </div>
                            <div className="stat-icon-wrapper" style={{ background: '#ECFDF5', color: '#10B981' }}>
                                <UserOutlined />
                            </div>
                        </Space>
                    </Card>
                </Col>

                <Col xs={24} sm={12} lg={8}>
                    <Card className="hover-lift" style={{ borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                        <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }}>
                            <div>
                                <Typography.Text style={{ color: '#64748B', fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: 4 }}>
                                    VENDOR ONBOARDING STATUS
                                </Typography.Text>
                                <Statistic
                                    value={suppliers.length > 0 ? "Active" : "None"}
                                    valueStyle={{ color: '#8B5CF6', fontWeight: 800, fontSize: '20px' }}
                                />
                            </div>
                            <div className="stat-icon-wrapper" style={{ background: '#F3E8FF', color: '#8B5CF6' }}>
                                <UserAddOutlined />
                            </div>
                        </Space>
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                <Col xs={24} lg={9}>
                    <Card 
                        title={
                            <Space size="middle">
                                <UserAddOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
                                <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Add Supplier Profile</span>
                            </Space>
                        }
                        style={{ borderRadius: '16px' }}
                    >
                        <Form
                            form={form}
                            onFinish={handleSubmit}
                            layout="vertical"
                        >
                            <Form.Item
                                name="name"
                                label="Supplier Company / Name"
                                rules={[{ required: true, message: 'Please enter the supplier name' }]}
                            >
                                <Input 
                                    placeholder="e.g. Unique Paper Mill Ltd." 
                                    prefix={<UserOutlined style={{ color: '#94A3B8' }} />}
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item
                                name="number"
                                label="Primary Contact Phone"
                                rules={[{ required: true, message: 'Please enter contact phone' }]}
                            >
                                <Input 
                                    placeholder="Contact phone number" 
                                    prefix={<PhoneOutlined style={{ color: '#94A3B8' }} />}
                                    size="large"
                                />
                            </Form.Item>

                            <Form.Item
                                name="address"
                                label="Supplier Office / Factory Address"
                            >
                                <Input.TextArea 
                                    rows={4} 
                                    placeholder="Registered vendor address" 
                                    style={{ borderRadius: '10px' }}
                                />
                            </Form.Item>

                            <Form.Item style={{ marginBottom: 0, marginTop: '20px' }}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    icon={<UserAddOutlined />}
                                    size="large"
                                    style={{ width: '100%', borderRadius: '10px' }}
                                >
                                    Register Supplier
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
                
                <Col xs={24} lg={15}>
                    <Card 
                        title={
                            <Space size="middle">
                                <UserOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
                                <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Supplier Directory</span>
                            </Space>
                        }
                        style={{ borderRadius: '16px' }}
                    >
                        <Table
                            columns={columns}
                            dataSource={suppliers}
                            rowKey="id"
                            pagination={{
                                pageSize: 10,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} suppliers`
                            }}
                            scroll={{ x: 700 }}
                            size="middle"
                        />
                    </Card>
                </Col>
            </Row>

            <Modal
                title={
                    <Space size="middle">
                        <EditOutlined style={{ color: '#4F46E5' }} />
                        <span style={{ fontWeight: 700 }}>Edit Supplier Info</span>
                    </Space>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                destroyOnClose
                width={500}
                style={{ borderRadius: '16px' }}
            >
                <Form
                    form={editForm}
                    onFinish={handleEditSubmit}
                    layout="vertical"
                >
                    <Form.Item
                        name="name"
                        label="Supplier Name"
                        rules={[{ required: true, message: 'Please enter the supplier name' }]}
                    >
                        <Input 
                            placeholder="Supplier name" 
                            prefix={<UserOutlined style={{ color: '#94A3B8' }} />}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="number"
                        label="Contact Phone"
                        rules={[{ required: true, message: 'Please enter contact phone' }]}
                    >
                        <Input 
                            placeholder="Phone number" 
                            prefix={<PhoneOutlined style={{ color: '#94A3B8' }} />}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        name="address"
                        label="Address"
                    >
                        <Input.TextArea 
                            rows={4} 
                            placeholder="Supplier address" 
                            style={{ borderRadius: '10px' }}
                        />
                    </Form.Item>

                    <Form.Item style={{ marginBottom: 0, marginTop: '20px' }}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button
                                onClick={() => setModalVisible(false)}
                                size="large"
                                style={{ borderRadius: '10px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                icon={<EditOutlined />}
                                size="large"
                                style={{ borderRadius: '10px' }}
                            >
                                Save Changes
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default AddSupplier;

